/**
 * Theme Configuration System
 * 
 * 5 Themes Available:
 * 1. Emerald (Default) - Agricultural green theme
 * 2. Ocean - Cool blue theme
 * 3. Sunset - Warm orange theme
 * 4. Lavender - Soft purple theme
 * 5. Monochrome - Clean grayscale theme
 * 
 * Each theme has both dark and light mode variants.
 */

export type ThemeMode = 'dark' | 'light' | 'system';
export type ThemeName = 'emerald' | 'ocean' | 'sunset' | 'lavender' | 'monochrome';

export interface ThemeColors {
  primary: string;
  primaryForeground: string;
  ring: string;
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
  success: string;
  warning: string;
  info: string;
  glow: string;
}

export interface Theme {
  name: ThemeName;
  label: string;
  description: string;
  colors: {
    dark: ThemeColors;
    light: ThemeColors;
  };
}

export const THEMES: Theme[] = [
  {
    name: 'emerald',
    label: 'Zümrüt',
    description: 'Tarım ve doğayla uyumlu yeşil tema',
    colors: {
      dark: {
        primary: '#10B981',
        primaryForeground: '#022C22',
        ring: '#10B981',
        chart1: '#10B981',
        chart2: '#3B82F6',
        chart3: '#F59E0B',
        chart4: '#EF4444',
        chart5: '#8B5CF6',
        success: '#10B981',
        warning: '#F59E0B',
        info: '#3B82F6',
        glow: 'rgba(16, 185, 129, 0.15)',
      },
      light: {
        primary: '#059669',
        primaryForeground: '#FFFFFF',
        ring: '#059669',
        chart1: '#059669',
        chart2: '#2563EB',
        chart3: '#D97706',
        chart4: '#DC2626',
        chart5: '#7C3AED',
        success: '#059669',
        warning: '#D97706',
        info: '#2563EB',
        glow: 'rgba(5, 150, 105, 0.1)',
      },
    },
  },
  {
    name: 'ocean',
    label: 'Okyanus',
    description: 'Sakin ve profesyonel mavi tema',
    colors: {
      dark: {
        primary: '#3B82F6',
        primaryForeground: '#EFF6FF',
        ring: '#3B82F6',
        chart1: '#3B82F6',
        chart2: '#10B981',
        chart3: '#F59E0B',
        chart4: '#EF4444',
        chart5: '#8B5CF6',
        success: '#10B981',
        warning: '#F59E0B',
        info: '#3B82F6',
        glow: 'rgba(59, 130, 246, 0.15)',
      },
      light: {
        primary: '#2563EB',
        primaryForeground: '#FFFFFF',
        ring: '#2563EB',
        chart1: '#2563EB',
        chart2: '#059669',
        chart3: '#D97706',
        chart4: '#DC2626',
        chart5: '#7C3AED',
        success: '#059669',
        warning: '#D97706',
        info: '#2563EB',
        glow: 'rgba(37, 99, 235, 0.1)',
      },
    },
  },
  {
    name: 'sunset',
    label: 'Günbatımı',
    description: 'Sicak ve enerjik turuncu tema',
    colors: {
      dark: {
        primary: '#F97316',
        primaryForeground: '#431407',
        ring: '#F97316',
        chart1: '#F97316',
        chart2: '#3B82F6',
        chart3: '#10B981',
        chart4: '#EF4444',
        chart5: '#8B5CF6',
        success: '#10B981',
        warning: '#F59E0B',
        info: '#3B82F6',
        glow: 'rgba(249, 115, 22, 0.15)',
      },
      light: {
        primary: '#EA580C',
        primaryForeground: '#FFFFFF',
        ring: '#EA580C',
        chart1: '#EA580C',
        chart2: '#2563EB',
        chart3: '#059669',
        chart4: '#DC2626',
        chart5: '#7C3AED',
        success: '#059669',
        warning: '#D97706',
        info: '#2563EB',
        glow: 'rgba(234, 88, 12, 0.1)',
      },
    },
  },
  {
    name: 'lavender',
    label: 'Lavanta',
    description: 'Yumusak ve modern mor tema',
    colors: {
      dark: {
        primary: '#8B5CF6',
        primaryForeground: '#F5F3FF',
        ring: '#8B5CF6',
        chart1: '#8B5CF6',
        chart2: '#3B82F6',
        chart3: '#10B981',
        chart4: '#EF4444',
        chart5: '#F59E0B',
        success: '#10B981',
        warning: '#F59E0B',
        info: '#3B82F6',
        glow: 'rgba(139, 92, 246, 0.15)',
      },
      light: {
        primary: '#7C3AED',
        primaryForeground: '#FFFFFF',
        ring: '#7C3AED',
        chart1: '#7C3AED',
        chart2: '#2563EB',
        chart3: '#059669',
        chart4: '#DC2626',
        chart5: '#D97706',
        success: '#059669',
        warning: '#D97706',
        info: '#2563EB',
        glow: 'rgba(124, 58, 237, 0.1)',
      },
    },
  },
  {
    name: 'monochrome',
    label: 'Monokrom',
    description: 'Minimalist siyah-beyaz tema',
    colors: {
      dark: {
        primary: '#A1A1AA',
        primaryForeground: '#18181B',
        ring: '#71717A',
        chart1: '#A1A1AA',
        chart2: '#71717A',
        chart3: '#52525B',
        chart4: '#3F3F46',
        chart5: '#27272A',
        success: '#A1A1AA',
        warning: '#71717A',
        info: '#52525B',
        glow: 'rgba(161, 161, 170, 0.15)',
      },
      light: {
        primary: '#52525B',
        primaryForeground: '#FFFFFF',
        ring: '#71717A',
        chart1: '#52525B',
        chart2: '#71717A',
        chart3: '#A1A1AA',
        chart4: '#D4D4D8',
        chart5: '#E4E4E7',
        success: '#52525B',
        warning: '#71717A',
        info: '#A1A1AA',
        glow: 'rgba(82, 82, 91, 0.1)',
      },
    },
  },
];

export const DEFAULT_THEME: ThemeName = 'emerald';
export const DEFAULT_MODE: ThemeMode = 'dark';

export function getTheme(name: ThemeName): Theme {
  return THEMES.find((t) => t.name === name) ?? THEMES[0]!;
}

export function getThemeColors(name: ThemeName, mode: 'dark' | 'light'): ThemeColors {
  const theme = getTheme(name);
  return theme.colors[mode];
}
